<template>
  <div class="container-fluid">
    <div class="row" id="app">
      <!-- Inputs Section -->
      <div class="col-12 col-lg-6 engineering-paper">
        <TopbarNav />
        <hr/>
        
        <!-- Section 1: Concrete Properties -->
        <h5>Concrete Properties</h5>
        <div class="row">
          <div class="col-6">
            <label for="phi_c">Resistance Factor for Concrete, φ<sub>c</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="phi_c" v-model="phi_c" step="0.01" />
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="fc_prime">Compressive Strength, f'<sub>c</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="fc_prime" v-model="fc_prime" step="1" /><span>MPa</span>
          </div>
        </div>
        <hr/>
        
        <!-- Section 2: Steel Properties -->
        <h5>Steel Properties</h5>
        <div class="row">
          <div class="col-6">
            <label for="phi_s">Resistance Factor for Steel, φ<sub>s</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="phi_s" v-model="phi_s" step="0.01" />
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="fy">Yield Strength, f<sub>y</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="fy" v-model="fy" step="1" /><span>MPa</span>
          </div>
        </div>
        <hr/>
        
        <!-- Section 3: Beam Dimensions -->
        <h5>Beam Dimensions</h5>
        <div class="row">
          <div class="col-6">
            <label for="b">Width, b:</label>
          </div>
          <div class="col-6">
            <input type="number" name="b" v-model="b" step="1" /><span>mm</span>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="h">Overall Depth, h:</label>
          </div>
          <div class="col-6">
            <input type="number" name="h" v-model="h" step="1" /><span>mm</span>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="As">Area of Steel, A<sub>s</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="As" v-model="As" step="1" /><span>mm<sup>2</sup></span>
          </div>
        </div>
        <div class="row" v-if="As < As_min || As > As_max">
          <div class="fst-italic alert alert-warning">
            <p v-if="As < As_min">
              A<sub>s</sub> = {{ As }} mm<sup>2</sup> is less than the minimum required reinforcement A<sub>s,min</sub> = {{ As_min.toFixed(2) }} mm<sup>2</sup>. The reinforcement fractures immediately upon first cracking.
            </p>
            <p v-if="As > As_max">
              A<sub>s</sub> = {{ As }} mm<sup>2</sup> exceeds the maximum allowed reinforcement A<sub>s,max</sub> = {{ As_max.toFixed(2) }} mm<sup>2</sup>. The concrete crushes on the compression face before the reinforcement yields if too high a value of reinforcement is used.
            </p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="cover">Concrete Cover:</label>
          </div>
          <div class="col-6">
            <input type="number" name="cover" v-model="cover" step="1" /><span>mm</span>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="db">Bar Diameter, d<sub>b</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="db" v-model="db" step="1" /><span>mm</span>
          </div>
        </div>
        <hr/>
        
        <!-- Section 4: Deflection Parameters -->
        <h5>Deflection Parameters</h5>
        <div class="row">
          <div class="col-6">
            <label for="L_n">Clear Span, L<sub>n</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="L_n" v-model="L_n" step="1" /><span>mm</span>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="wSDL">Sustained Super-imposed Dead Load, w<sub>SDL</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="wSDL" v-model="wSDL" step="0.1" /><span>kN/m</span>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="wL">Live Load, w<sub>L</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="wL" v-model="wL" step="0.1" /><span>kN/m</span>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="sustained_LL_percent">Sustained Live Load:</label>
          </div>
          <div class="col-6">
            <input type="number" name="sustained_LL_percent" v-model="sustained_LL_percent" step="1" min="0" max="100" /><span>%</span>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <label for="s">Load Duration Factor, s:</label>
          </div>
          <div class="col-6">
            <select name="s" v-model="s">
              <option value="2.0">Loads sustained for five years or more</option>
              <option value="1.4">Loads sustained for 12 months</option>
              <option value="1.2">Loads sustained for six months</option>
              <option value="1.0">Loads sustained for three months</option>
            </select>
          </div>
        </div>
        <hr/>
        
      </div>
      
      <!-- Report Section -->
      <div class="col-12 col-lg-6 engineering-paper">
        <h4>Singly-Reinforced Concrete Beam</h4>
        
        <!-- Alpha 1 and Beta 1 Calculations -->
        <h5>Stress Block Parameters</h5>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\alpha_1 = 0.85 - 0.0015f\'_c \\geq 0.67'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\alpha_1 = ' + alpha_1.toFixed(2)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Based on f'<sub>c</sub> = {{ fc_prime }} MPa</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\beta_1 = 0.97 - 0.0025f\'_c \\geq 0.67'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\beta_1 = ' + beta_1.toFixed(2)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Based on f'<sub>c</sub> = {{ fc_prime }} MPa</p>
          </div>
        </div>
        <hr/>
        
        <!-- c and a Calculations -->
        <h5>Neutral Axis Depth</h5>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'c = \\frac{A_s \\phi_s f_y}{\\alpha_1 \\phi_c f\'_c b}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'c = ' + c.toFixed(2) + ' \\text{ mm}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Depth to neutral axis</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'a = \\beta_1 \\cdot c'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'a = ' + a.toFixed(2) + ' \\text{ mm}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Depth of equivalent rectangular stress block</p>
          </div>
        </div>
        <hr/>
        
        <!-- Mr Calculation -->
        <h5>Factored Moment Resistance</h5>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'d = h - \\text{cover} - \\frac{d_b}{2}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'d = ' + d.toFixed(2) + ' \\text{ mm}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Effective depth</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'M_r = A_s \\cdot \\phi_s \\cdot f_y \\cdot (d - a/2)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'M_r = ' + Mr.toFixed(2) + ' \\text{ kN} \\cdot \\text{m}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Factored moment resistance</p>
          </div>
        </div>
        <hr/>
        
        <!-- A_smax and A_smin Calculations -->
        <h5>Reinforcement Limits</h5>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'A_{s,max} = \\frac{\\alpha_1 \\phi_c f\'_c \\beta_1}{\\phi_s f_y} \\left[ \\frac{700}{700 + f_y} \\right] b \\cdot d'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'A_{s,max} = ' + As_max.toFixed(2) + ' \\text{ mm}^2'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Maximum area of steel reinforcement</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'A_{s,min} = b \\cdot h \\cdot \\frac{0.2\\sqrt{f\'_c}}{f_y}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'A_{s,min} = ' + As_min.toFixed(2) + ' \\text{ mm}^2'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Minimum area of steel reinforcement</p>
          </div>
        </div>
        <hr/>
        
        <!-- Long-term Deflection -->
        <h5>Long-term Deflection</h5>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'w_{DL} = b \\cdot h \\cdot \\gamma_c'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'w_{DL} = ' + wDL.toFixed(2) + ' \\text{ kN/m}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Self-weight of beam (γ<sub>c</sub> = 24 kN/m³)</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\zeta_s = \\left[ 1 + \\frac{s}{1 + 50\\rho\'} \\right]'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\zeta_s = ' + zeta_s.toFixed(2)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Long-term deflection factor (ρ' = 0 for singly-reinforced beam)</p>
          </div>
        </div>
        <hr/>
        
      </div>
    </div>
  </div>
</template>

<script>
import TopbarNav from "@/components/TopbarNav.vue";

export default {
  name: "ConcreteBeam",
  components: { TopbarNav },
  data: function () {
    return {
      phi_c: 0.65,
      fc_prime: 30,
      phi_s: 0.85,
      fy: 400,
      b: 300,
      h: 500,
      As: 1000,
      cover: 40,
      db: 20,
      L_n: 6000,
      wSDL: 10,
      wL: 5,
      sustained_LL_percent: 25,
      s: "2.0",
    };
  },
  computed: {
    alpha_1: function() {
      return Math.max(0.85 - 0.0015 * this.fc_prime, 0.67);
    },
    beta_1: function() {
      return Math.max(0.97 - 0.0025 * this.fc_prime, 0.67);
    },
    c: function() {
      return (this.As * this.phi_s * this.fy) / (this.alpha_1 * this.phi_c * this.fc_prime * this.b);
    },
    a: function() {
      return this.beta_1 * this.c;
    },
    d: function() {
      return this.h - this.cover - this.db / 2;
    },
    Mr: function() {
      // Mr in N·mm, convert to kN·m
      return (this.As * this.phi_s * this.fy * (this.d - this.a / 2)) / 1000000;
    },
    As_max: function() {
      return (this.alpha_1 * this.phi_c * this.fc_prime * this.beta_1 / (this.phi_s * this.fy)) * 
             (700 / (700 + this.fy)) * this.b * this.d;
    },
    As_min: function() {
      return this.b * this.h * (0.2 * Math.sqrt(this.fc_prime) / this.fy);
    },
    wDL: function() {
      // b and h are in mm, convert to m: (b/1000) * (h/1000) * 24 kN/m³
      return (this.b / 1000) * (this.h / 1000) * 24;
    },
    rho_prime: function() {
      // For singly-reinforced beam, rho' = 0
      return 0;
    },
    zeta_s: function() {
      return 1 + (parseFloat(this.s) / (1 + 50 * this.rho_prime));
    },
  },
};
</script>

<style scoped>
</style>